import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import MarketingShell from "@/components/MarketingShell";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { PHOTOS } from "@/lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  { value: "Lifecycle Event Booking", label: "Lifecycle Event Booking" },
  { value: "Community Event Booking", label: "Community Event Booking" },
  { value: "Commission Request", label: "Commission Request" },
  { value: "Press / Media", label: "Press / Media" },
  { value: "Other", label: "Other" },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  // Deep links from the site can preset the inquiry type and seed the note,
  // e.g. /contact?type=Community Event Booking&topic=Shabbaton
  const presetType = INQUIRY_TYPES.find(
    (t) => t.value.toLowerCase() === (searchParams.get("type") || "").trim().toLowerCase()
  )?.value;
  const presetTopic = (searchParams.get("topic") || "").trim().slice(0, 80);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inquiryType, setInquiryType] = useState(presetType || "");
  const [place, setPlace] = useState(null);
  const [eventDate, setEventDate] = useState("");
  const [message, setMessage] = useState(presetTopic ? `${presetTopic}: ` : "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isBooking = inquiryType.includes("Booking");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Need a name and an email to write back.");
      return;
    }
    if (!inquiryType) {
      toast.error("Pick what kind of inquiry this is so it lands in the right inbox.");
      return;
    }
    if (isBooking && !place) {
      toast.error("Pick the event's city from the suggestions so Dan can work out travel.");
      return;
    }
    setSubmitting(true);
    try {
      // Lead capture, the source helps Dan segment in Systeme.io.
      await axios.post(`${API}/leads`, {
        email: email.trim().toLowerCase(),
        first_name: name.trim(),
        source: "contact-form",
      });
      // Full inquiry, saves the message body locally and pushes a
      // dynamic `CONTACT: [inquiry type]` tag to Systeme.io so Dan can
      // wire each type into its own automated email flow.
      await axios.post(`${API}/inquiries`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        message: message.trim(),
        inquiry_type: inquiryType,
        location: place?.display || "",
        city: place?.city || "",
        state: place?.state || "",
        country: place?.country || "",
        country_code: place?.country_code || "",
        event_date: isBooking ? eventDate : "",
      });
      setDone(true);
      toast.success("Got it, Dan will write you back.");
    } catch (err) {
      toast.error("Hmm, that didn't go through. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingShell active="/contact">
      <Toaster position="bottom-right" richColors />
      <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Get in touch
        </p>
        <h1
          className="leading-[0.95]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: "clamp(40px, 5.6vw, 84px)", textWrap: "balance", maxWidth: "18ch" }}
        >
          Let&apos;s make <em style={{ color: "#F2D589" }}>something</em>.
        </h1>
        <p className="mt-5 max-w-[44rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          Performances, soundbaths, custom commissions, an idea you
          can&apos;t quite articulate yet, this is the place.
          A short note about who, what, and when is plenty. I&apos;ll
          write back.
        </p>
      </motion.section>

      <section className="mt-12 grid md:grid-cols-[1fr_1fr] gap-8 items-start">
        <div
          className="rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(255,255,255,0.12)", aspectRatio: "4 / 5" }}
          data-testid="contact-hero-photo"
        >
          <img
            src={PHOTOS.danPortrait}
            alt="Dan at the piano"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="rounded-2xl border p-6 sm:p-8 backdrop-blur-md" style={{ borderColor: "rgba(255,255,255,0.18)", backgroundColor: "rgba(8,4,25,0.55)" }}>
          {done ? (
            <div className="text-center py-8">
              <p className="text-3xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                Sent. Talk soon.
              </p>
              <p className="text-sm" style={{ color: "rgba(245,239,227,0.65)" }}>
                Check your inbox, Dan or someone close will reply within a couple of days.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="contact-form">
              <Input
                type="text"
                required
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="contact-name"
                className="h-12 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
              />
              <Input
                type="email"
                required
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="contact-email"
                autoComplete="email"
                className="h-12 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
              />
              <Select value={inquiryType} onValueChange={setInquiryType}>
                <SelectTrigger
                  data-testid="contact-inquiry-type"
                  className="h-12 bg-white/[0.06] border-white/15 text-white data-[placeholder]:text-white/40"
                >
                  <SelectValue placeholder="What kind of inquiry?" />
                </SelectTrigger>
                <SelectContent
                  className="border-white/15 text-white"
                  style={{ backgroundColor: "#0C0826" }}
                >
                  {INQUIRY_TYPES.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`contact-inquiry-option-${opt.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="focus:bg-white/10 focus:text-[#F2D589]"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isBooking && (
                <div className="flex flex-col gap-3" data-testid="contact-booking-fields">
                  <LocationAutocomplete
                    value={place}
                    onSelect={setPlace}
                    testIdPrefix="contact-location"
                    placeholder="Where's the event? (city / state / country)"
                  />
                  <div>
                    <label
                      htmlFor="contact-event-date"
                      className="block text-[10px] uppercase tracking-[0.24em] mb-2"
                      style={{ color: "rgba(245,239,227,0.55)" }}
                    >
                      Event date (if you know it)
                    </label>
                    <Input
                      id="contact-event-date"
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      data-testid="contact-event-date"
                      className="h-12 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
              )}
              <Textarea
                placeholder="Who, what, when. Anything else I should know."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="contact-message"
                rows={6}
                className="bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
              />
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="h-12 rounded-full mt-1"
                style={{
                  backgroundColor: "transparent",
                  color: "#F2D589",
                  border: "1px solid #F2D589",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                {submitting ? "Sending…" : "Send"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
